import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

export interface SiteCamera {
  id: number;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  /** 1=online, 0=offline desde ``devices_cameras_status`` (``GET /sites/map``). */
  state?: number | null;
}

/** Cámara en línea según snapshot del mapa (``state === 1``). */
export function isSiteCameraOnline(cam: Pick<SiteCamera, 'state'>): boolean {
  return cam.state === 1;
}

export function onlineSiteCameras(
  cameras: readonly SiteCamera[] | null | undefined,
): SiteCamera[] {
  return (cameras ?? []).filter((c) => c.id && isSiteCameraOnline(c));
}

export interface Site {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  address: string;
  status: 'online' | 'offline' | 'partial' | 'maintenance';
  serviceName: string;
  stateId: number | null;
  lastStatusId?: number | null;
  stateName: string | null;
  active: boolean;
  certificate: boolean;
  cameras?: SiteCamera[];
}

export type MapAlignedOperational = 'on' | 'issue' | 'maint' | 'off' | 'unknown';

type CameraRow = Readonly<{ state?: number | null }>;

function cameraOperationalSummary(
  cameras: readonly CameraRow[] | null | undefined
): 'on' | 'issue' | 'off' | null {
  const cams: readonly CameraRow[] = (cameras ?? []).filter((c) => c.state === 0 || c.state === 1);
  if (cams.length === 0) return null;
  const online: number = cams.filter((c) => c.state === 1).length;
  if (online === cams.length) return 'on';
  if (online === 0) return 'off';
  return 'issue';
}

export function siteMapAlignedOperational(site: {
  stateId?: number | null;
  cameras?: readonly CameraRow[] | null;
}): MapAlignedOperational {
  const fromCams: 'on' | 'issue' | 'off' | null = cameraOperationalSummary(site.cameras);
  if (fromCams != null) {
    if (fromCams === 'on') return 'on';
    if (fromCams === 'off') return 'off';
    return 'issue';
  }

  const sid: number | null = site.stateId ?? null;
  if (sid === 1) return 'on';
  if (sid === 2) return 'issue';
  if (sid === 3) return 'maint';
  if (sid === 4) return 'off';
  return 'unknown';
}

export function siteOperationalMarkerColor(stateId: number | null | undefined): string {
  switch (stateId) {
    case 1:
      return '#22c55e';
    case 2:
      return '#f97316';
    case 3:
      return '#2563eb';
    case 4:
      return '#dc2626';
    default:
      return '#64748b';
  }
}

type SiteCameraRow = Readonly<{ state?: number | null }>;

type SiteOperationalRow = Readonly<{
  stateId?: number | null;
  devices?: { cameras?: readonly SiteCameraRow[] | null } | null;
}>;

/** Conteo de sitios por semáforo operativo (``siteMapAlignedOperational``). */
export function deriveSitesOperationalStats(
  sites: ReadonlyArray<SiteOperationalRow>
): { total: number; online: number; partial: number; offline: number } {
  let online = 0;
  let partial = 0;
  let offline = 0;
  for (const site of sites) {
    const aligned = siteMapAlignedOperational({
      stateId: site.stateId,
      cameras: site.devices?.cameras ?? [],
    });
    if (aligned === 'on') {
      online += 1;
    } else if (aligned === 'issue') {
      partial += 1;
    } else {
      offline += 1;
    }
  }
  return { total: sites.length, online, partial, offline };
}

export function siteMapMarkerColor(site: Site): string {
  const op: MapAlignedOperational = siteMapAlignedOperational(site);
  switch (op) {
    case 'on':
      return '#22c55e';
    case 'issue':
      return '#f97316';
    case 'maint':
      return '#2563eb';
    case 'off':
      return '#dc2626';
    default:
      return '#64748b';
  }
}

export const fetchSites = async (): Promise<Site[]> => {
  try {
    const token: string | null = localStorage.getItem('token');
    const refreshToken: string | null = localStorage.getItem('refreshToken');
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await axios.get<{ data?: Site[] }>(`${API_URL}/sites/map`, { headers });
    return response.data?.data ?? [];
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 401) {
      console.warn('Usuario no autenticado. Por favor, inicie sesión.');
    }
    return [];
  }
};
